<template>
  <div class="forgot-wrap">
    <!-- 步骤指示器 -->
    <div v-if="step !== 'success'" class="steps">
      <div
        v-for="(label, i) in ['账号信息', '验证码', '重置密码']"
        :key="i"
        class="step-item"
        :class="{ active: step >= i + 1 }"
      >
        <div class="step-dot">{{ i + 1 }}</div>
        <div class="step-label">{{ label }}</div>
      </div>
      <div class="step-line step-line-1" :class="{ active: step >= 2 }"></div>
      <div class="step-line step-line-2" :class="{ active: step >= 3 }"></div>
    </div>

    <!-- Step 1: 账号信息 -->
    <a-form-model
      v-if="step === 1"
      ref="form1"
      :model="fd"
      :rules="rules1"
      :label-col="{ span: 24 }"
      :wrapper-col="{ span: 24 }"
    >
      <a-form-model-item
        v-if="!isInstitution"
        label="重置方式"
        prop="resetMethod"
      >
        <a-radio-group
          v-model="fd.resetMethod"
          @change="onMethodChange"
          class="method-group"
        >
          <a-radio value="email">邮箱重置密码</a-radio>
          <a-radio value="phone">手机号重置密码</a-radio>
        </a-radio-group>
      </a-form-model-item>

      <a-form-model-item
        :label="fd.resetMethod === 'email' ? '您的邮箱号' : '您的手机号'"
        prop="account"
      >
        <a-input
          v-model="fd.account"
          :placeholder="
            fd.resetMethod === 'email' ? '请输入您的邮箱' : '请输入您的手机号'
          "
          size="large"
        />
      </a-form-model-item>

      <a-form-model-item
        v-if="!isInstitution"
        label="请输入验证码"
        prop="captcha"
      >
        <div class="captcha-row">
          <a-input
            v-model="fd.captcha"
            placeholder="请输入图形验证码"
            size="large"
            style="flex: 1"
          />
          <div class="captcha-img" @click="getCaptcha">
            <img v-if="captchaUrl" :src="captchaUrl" alt="验证码" />
            <span v-else class="captcha-placeholder">点击获取</span>
          </div>
        </div>
      </a-form-model-item>

      <div class="actions">
        <a-button size="large" class="btn-cancel" @click="$emit('close')"
          >取消</a-button
        >
        <a-button
          type="primary"
          size="large"
          :loading="loading"
          @click="handleStep1"
          >下一步</a-button
        >
      </div>
    </a-form-model>

    <!-- Step 2: 验证码 -->
    <a-form-model
      v-if="step === 2"
      ref="form2"
      :model="fd"
      :rules="rules2"
      :label-col="{ span: 24 }"
      :wrapper-col="{ span: 24 }"
    >
      <div class="account-info-box">
        <span class="info-label"
          >{{ fd.resetMethod === "email" ? "邮箱" : "手机号" }}：</span
        >
        <span class="info-value">{{ fd.account }}</span>
        <a-button
          type="link"
          size="small"
          :disabled="countdown > 0"
          :loading="resending"
          @click="resendCode"
        >
          {{ countdown > 0 ? `${countdown}s后重发` : "重新发送" }}
        </a-button>
      </div>
      <div v-if="codeSent" class="code-sent-tip">
        验证码已发送至{{
          fd.resetMethod === "email" ? "邮箱" : "手机号"
        }}，请注意查收！
      </div>

      <a-form-model-item prop="verifyCode">
        <a-input
          v-model="fd.verifyCode"
          :placeholder="`请填写${
            fd.resetMethod === 'email' ? '邮箱' : '手机号'
          }验证码`"
          size="large"
        />
      </a-form-model-item>

      <div class="actions">
        <a-button size="large" class="btn-cancel" @click="step = 1"
          >上一步</a-button
        >
        <a-button type="primary" size="large" @click="handleStep2"
          >下一步</a-button
        >
      </div>
    </a-form-model>

    <!-- Step 3: 重置密码 -->
    <a-form-model
      v-if="step === 3"
      ref="form3"
      :model="fd"
      :rules="rules3"
      :label-col="{ span: 24 }"
      :wrapper-col="{ span: 24 }"
    >
      <a-form-model-item label="新密码" prop="password">
        <a-input-password
          v-model="fd.password"
          placeholder="请输入密码"
          size="large"
          @input="calcStrength"
        />
        <div class="strength-wrap">
          <div class="strength-bar">
            <div class="s-item" :class="{ low: strength >= 1 }"></div>
            <div class="s-item" :class="{ medium: strength >= 2 }"></div>
            <div class="s-item" :class="{ high: strength >= 3 }"></div>
          </div>
          <div class="strength-hint">
            {{
              isInstitution
                ? "密码长度为12-30位，且必须包含大小写字母、数字及特殊符号"
                : "密码长度至少8位以上，建议混合使用大小写字母、数字及特殊符号"
            }}
          </div>
        </div>
      </a-form-model-item>

      <a-form-model-item label="确认密码" prop="confirmPassword">
        <a-input-password
          v-model="fd.confirmPassword"
          placeholder="请确认密码"
          size="large"
        />
      </a-form-model-item>

      <div class="actions">
        <a-button size="large" class="btn-cancel" @click="step = 2"
          >上一步</a-button
        >
        <a-button
          type="primary"
          size="large"
          :loading="loading"
          @click="handleStep3"
          >完成</a-button
        >
      </div>
    </a-form-model>

    <!-- 成功 -->
    <div v-if="step === 'success'" class="success-wrap">
      <a-icon type="check-circle" theme="filled" class="success-icon" />
      <h3 class="success-title">密码重置成功！</h3>
      <p class="success-desc">您的密码已成功重置，请使用新密码登录。</p>
      <a-button
        type="primary"
        size="large"
        class="btn-login"
        @click="$emit('close')"
        >返回登录</a-button
      >
    </div>

    <!-- 返回登录 -->
    <div v-if="step !== 'success'" class="back-link">
      <a @click="$emit('close')">返回登录</a>
    </div>
  </div>
</template>

<script>
import api from "@/plugins/api";

export default {
  name: "ForgotPasswordForm",
  props: {
    role: { type: String, default: null },
  },
  data() {
    const validateAccount = (_rule, value, callback) => {
      if (!value) return callback(new Error("请输入"));
      if (this.fd.resetMethod === "email") {
        if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
          return callback(new Error("请输入正确的邮箱地址"));
        }
      } else {
        if (!/^1[3-9]\d{9}$/.test(value)) {
          return callback(new Error("请输入正确的手机号"));
        }
      }
      callback();
    };
    const validatePassword = (_rule, value, callback) => {
      if (!value) return callback(new Error("请输入密码"));
      if (this.isInstitution) {
        if (value.length < 12 || value.length > 30) {
          return callback(new Error("密码长度为12-30位"));
        }
        if (
          !/[A-Z]/.test(value) ||
          !/[a-z]/.test(value) ||
          !/\d/.test(value) ||
          !/[^A-Za-z0-9]/.test(value)
        ) {
          return callback(new Error("密码必须包含大小写字母、数字及特殊符号"));
        }
        return callback();
      }
      if (value.length < 8) return callback(new Error("密码长度至少8位以上"));
      if (!/^(?=.*[a-zA-Z])(?=.*\d).+$/.test(value))
        return callback(new Error("密码必须包含字母和数字"));
      callback();
    };
    const validateConfirm = (_rule, value, callback) => {
      if (!value) return callback(new Error("请确认密码"));
      if (value !== this.fd.password)
        return callback(new Error("两次输入的密码不一致"));
      callback();
    };
    return {
      step: 1,
      loading: false,
      resending: false,
      countdown: 0,
      codeSent: true,
      captchaUrl: "",
      captchaEnabled: this.role !== "institution",
      strength: 0,
      timer: null,
      fd: {
        resetMethod: this.role === "institution" ? "phone" : "email",
        account: "",
        captcha: "",
        uuid: "",
        verifyCode: "",
        password: "",
        confirmPassword: "",
      },
      rules1: {
        account: [
          { required: true, validator: validateAccount, trigger: "blur" },
        ],
        captcha: [
          { required: true, message: "请输入图形验证码", trigger: "blur" },
          { min: 4, max: 6, message: "验证码长度为4-6位", trigger: "blur" },
        ],
      },
      rules2: {
        verifyCode: [
          { required: true, message: "请输入验证码", trigger: "blur" },
          { min: 4, max: 6, message: "验证码长度为4-6位", trigger: "blur" },
        ],
      },
      rules3: {
        password: [
          { required: true, validator: validatePassword, trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, validator: validateConfirm, trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    isInstitution() {
      return this.role === "institution";
    },
    loginApi() {
      return this.isInstitution
        ? api().institution.login
        : this.role === "smo"
        ? api().smo.login
        : this.role === "agent"
        ? api().agent.login
        : api().sponsor.login;
    },
  },
  mounted() {
    if (!this.isInstitution) this.getCaptcha();
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    getCaptcha() {
      if (this.isInstitution) return;
      this.loginApi.getCodeImg({ scene: "forgot_password" }).then((res) => {
        if (this.role === "agent") {
          this.captchaEnabled = true;
          this.captchaUrl = res.data.captcha_image;
          this.fd.uuid = res.data.captcha_key;
        } else {
          this.captchaEnabled = res.captchaEnabled !== false;
          if (this.captchaEnabled) {
            this.captchaUrl = "data:image/gif;base64," + res.img;
            this.fd.uuid = res.uuid;
          }
        }
      });
    },
    onMethodChange() {
      this.fd.account = "";
      this.fd.captcha = "";
      this.fd.uuid = "";
      this.$refs.form1 && this.$refs.form1.clearValidate();
      if (!this.isInstitution) this.getCaptcha();
    },
    handleStep1() {
      this.$refs.form1.validate((valid) => {
        if (!valid) return;
        let params = {
          type: this.fd.resetMethod === "email" ? "2" : "1",
          account: this.fd.account,
        };
        if (this.captchaEnabled) {
          params.uuid = this.fd.uuid;
          params.code = this.fd.captcha;
        }
        if (this.isInstitution) {
          params = { mobile: this.fd.account };
        } else if (this.role === "agent") {
          params = {
            type: this.fd.resetMethod === "email" ? "email" : "sms",
            email: this.fd.resetMethod === "email" ? this.fd.account : "",
            mobile: this.fd.resetMethod !== "email" ? this.fd.account : "",
            captcha_key: this.fd.uuid,
            captcha_code: this.fd.captcha,
          };
        }
        this.loading = true;
        this.loginApi
          .sendResetCode(params)
          .then(() => {
            this.$message.success("验证码已发送，请查收");
            this.codeSent = true;
            this.startCountdown();
            this.step = 2;
          })
          .catch(() => {
            if (this.captchaEnabled) {
              this.getCaptcha();
              this.fd.captcha = "";
            }
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    handleStep2() {
      this.$refs.form2.validate((valid) => {
        if (valid) this.step = 3;
      });
    },
    handleStep3() {
      this.$refs.form3.validate((valid) => {
        if (!valid) return;
        this.loading = true;
        let params = {
          code: this.fd.verifyCode,
          password: this.fd.password,
        };
        if (this.isInstitution) {
          params = {
            mobile: this.fd.account,
            captcha: this.fd.verifyCode,
            password: this.fd.password,
          };
        } else if (this.role === "agent") {
          params = {
            type: this.fd.resetMethod === "email" ? "email" : "sms",
            email: this.fd.resetMethod === "email" ? this.fd.account : "",
            mobile: this.fd.resetMethod !== "email" ? this.fd.account : "",
            email_code:
              this.fd.resetMethod === "email" ? this.fd.verifyCode : "",
            sms_code: this.fd.resetMethod !== "email" ? this.fd.verifyCode : "",
            captcha_key: this.fd.uuid,
            password: this.fd.password,
          };
        }
        this.loginApi
          .resetPasswordByCode(params)
          .then(() => {
            this.$message.success("密码重置成功");
            this.step = "success";
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    resendCode() {
      if (this.countdown > 0 || this.resending) return;
      this.resending = true;
      let params = {
        type: this.fd.resetMethod === "email" ? "2" : "1",
        account: this.fd.account,
      };
      if (this.isInstitution) {
        params = { mobile: this.fd.account };
      } else if (this.role === "agent") {
        params = {
          type: this.fd.resetMethod === "email" ? "email" : "sms",
          email: this.fd.resetMethod === "email" ? this.fd.account : "",
          mobile: this.fd.resetMethod !== "email" ? this.fd.account : "",
          captcha_key: this.fd.uuid,
          captcha_code: this.fd.captcha,
        };
      }
      this.loginApi
        .sendResetCode(params)
        .then(() => {
          this.codeSent = true;
          this.startCountdown();
          this.$message.success("验证码已重新发送，请查收");
        })
        .finally(() => {
          this.resending = false;
        });
    },
    startCountdown() {
      this.countdown = 60;
      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
    },
    calcStrength() {
      const p = this.fd.password;
      if (!p || p.length < (this.isInstitution ? 12 : 8)) {
        this.strength = 0;
        return;
      }
      const types = [
        /[a-z]/,
        /[A-Z]/,
        /\d/,
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
      ].filter((r) => r.test(p)).length;
      this.strength = types >= 4 ? 3 : types >= 3 ? 2 : types >= 2 ? 1 : 0;
    },
  },
};
</script>

<style scoped>
.forgot-wrap {
  padding: 4px 0 8px;
}

/* 步骤指示器 */
.steps {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
}
.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #9ba9bb;
  font-size: 0.82rem;
  z-index: 1;
  width: 60px;
}
.step-item.active {
  color: #1477ff;
}
.step-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid #d8e0ea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-bottom: 6px;
  background: #fff;
}
.step-item.active .step-dot {
  border-color: #1477ff;
  color: #1477ff;
}
.step-line {
  position: absolute;
  top: 14px;
  height: 2px;
  background: #e8edf3;
  width: calc(50% - 60px);
}
.step-line-1 {
  left: 60px;
}
.step-line-2 {
  right: 60px;
}
.step-line.active {
  background: #1477ff;
}

/* 图形验证码 */
.captcha-row {
  display: flex;
  gap: 12px;
  align-items: center;
}
.captcha-img {
  width: 120px;
  height: 40px;
  border: 1px solid #d5dde8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f5f5f5;
  flex-shrink: 0;
  overflow: hidden;
}
.captcha-img:hover {
  border-color: #1477ff;
}
.captcha-img img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.captcha-placeholder {
  font-size: 12px;
  color: #999;
}

/* 账号信息框 */
.account-info-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 16px;
}
.info-label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
}
.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  flex: 1;
}
.code-sent-tip {
  font-size: 13px;
  color: #ff4d4f;
  margin-bottom: 12px;
  padding-left: 4px;
}

/* 密码强度 */
.strength-wrap {
  margin-top: 10px;
}
.strength-bar {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}
.s-item {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #f0f0f0;
  transition: background 0.3s;
}
.s-item.low {
  background: #ff4d4f;
}
.s-item.medium {
  background: #faad14;
}
.s-item.high {
  background: #52c41a;
}
.strength-hint {
  font-size: 12px;
  color: #ff4d4f;
  line-height: 1.5;
}

/* 操作按钮 */
.actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}
.actions .ant-btn {
  min-width: 110px;
  height: 42px;
  font-size: 15px;
  border-radius: 21px;
}
.btn-cancel {
  background: #f0f2f5;
  border-color: #d9d9d9;
  color: #666;
}
.btn-cancel:hover {
  background: #e6e6e6;
}

/* 成功页 */
.success-wrap {
  text-align: center;
  padding: 32px 0 16px;
}
.success-icon {
  font-size: 72px;
  color: #1477ff;
  margin-bottom: 20px;
}
.success-title {
  font-size: 22px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}
.success-desc {
  font-size: 14px;
  color: #888;
  line-height: 1.6;
  margin-bottom: 32px;
}
.btn-login {
  min-width: 140px;
  height: 42px;
  font-size: 15px;
  border-radius: 21px;
}

/* 返回登录 */
.back-link {
  text-align: center;
  margin-top: 16px;
}
.back-link a {
  font-size: 13px;
  color: #1477ff;
  cursor: pointer;
}
.back-link a:hover {
  text-decoration: underline;
}

/* 重置方式 */
.method-group {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
</style>
